import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

/**
 * QA-557 중문몰 위안(CNY) 병기 — FE(storefront #189)와 같은 규칙. 중문몰(zh-CN) 주문의 USD 금액 뒤에 " (≈¥xx.xx)".
 * 위안 = USD × (USD krwPerUnit ÷ CNY krwPerUnit), 소수 둘째 자리 반올림. 환율은 BO 환율 설정의 활성 값(주문 당시로 고정하지
 * 않는 참고가 — 환율을 바꾸면 과거 주문 표기도 바뀐다). 0, 다른 몰, USD 가 아닌 금액, 환율이 없으면 붙이지 않는다.
 */
export const CNY_MALL = 'zh-CN';

interface RateRow {
  currencyCode: string;
  krwPerUnit: number;
  active: boolean;
}

/** 1 USD 가 몇 위안인지 — USD·CNY 모두 활성·양수일 때만. */
export const cnyPerUsdOf = (rates: ReadonlyArray<RateRow> | null | undefined): number | null => {
  const find = (code: string) => (rates ?? []).find((r) => r.currencyCode === code && r.active && r.krwPerUnit > 0);
  const usd = find('USD');
  const cny = find('CNY');
  return usd && cny ? usd.krwPerUnit / cny.krwPerUnit : null;
};

/** 국가몰 코드 정규화 — BE 는 KO_KR/ZH_CN 또는 BCP-47 을 준다. */
const isCnyMall = (mall: string | null | undefined): boolean =>
  (mall ?? '').replace('_', '-').toLowerCase() === CNY_MALL.toLowerCase();

/** " (≈¥94.31)" — 붙일 대상이 아니면 "". amount 는 USD major 소수 문자열(또는 숫자). */
export const cnyReferenceSuffix = (
  amount: string | number,
  currencyCode: string,
  mall: string | null | undefined,
  cnyPerUsd: number | null,
): string => {
  if (cnyPerUsd == null || currencyCode.toUpperCase() !== 'USD' || !isCnyMall(mall)) return '';
  const usd = typeof amount === 'number' ? amount : Number(amount);
  if (!Number.isFinite(usd) || usd === 0) return '';
  const cny = Math.round(usd * cnyPerUsd * 100) / 100;
  return ` (≈¥${cny.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`;
};

/**
 * 활성 환율로 1 USD = 몇 CNY. enabled(화면에 중문몰 USD 주문이 있을 때)만 부른다. 권한 없음·실패면 null(병기 생략).
 * FE 와 같이 5분 캐시.
 */
export const useCnyPerUsd = (enabled: boolean): number | null => {
  const query = useQuery<RateRow[]>({
    queryKey: ['admin', 'exchange-rates'],
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await adminFetch('/admin/v1/exchange-rates');
      if (!res.ok) throw new Error(`exchange rates ${res.status}`);
      const env = (await res.json()) as { data?: RateRow[] | null };
      return env.data ?? [];
    },
  });
  return cnyPerUsdOf(query.data);
};
